import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {
  useChooseRouteByOrderIdMutation,
  useCurrentRoute,
  useSummary,
} from '../../../../../../../api/calc'
import {useState} from "react";

import './card.scss'
import {COLORS} from "../../../../../../../config/constants";
import { Check as CheckIcon } from '@vedmarket/ui-kit';
import Button from "@mui/material/Button";
import React from "react";
import Services from './services';
import {getTariffTerms} from './tariff-terms';
import Route from './route';
import ResultRoute from './result-route';
import { ExpandMore } from '@vedmarket/ui-kit';
import { Delete as DeleteIcon } from '@vedmarket/ui-kit';
import moment from "moment";
import {useGetCalcIdFromRoute} from "../../../../../../../utils/hooks";
import {getDemoDeliveryRouteButtonProps} from "components/cabinet/demo/steps/delivery/use-delivery";
import cx from "classnames";
import {isCalculationViewModeError, numberWithSpaces} from "../../../../../../../utils/helpers";
import { readOnlyButtonEndIcon } from 'components/common/read-only-action-icon';
import 'components/common/read-only-action-button.scss';

import {useCalculationReadOnly} from '../../../../calculation-read-only';
import { reachGoal } from 'utils/metrica';

const DeliveryCard = ({ isActive, route, refetch, handleRemove, handleClose, isChosen, noConfirmButton, isSearchResult, details } : DeliveryCardProps ) => {

  const [ detailsExpanded, setDetailsExpanded ] = useState(details?.defaultExpanded ?? false);

  const currentRoute = useCurrentRoute()
  const summary = useSummary()

  const [ activeAddServicesIds ] = useState<number[]>(currentRoute?.selected_services || [])

  const [ currentRouteId, setCurrentRouteId ] = useState<number | undefined>()

  const [ isChanged, setIsChanged ] = useState(false)

  const { t } = useTranslation()

  const BENEFITS: Record<string, { text: string, background: string }> = {
    'fastest': {
      text: t('calc__text__delivery__theFastest'),
      background: '#9575CD',
    },
    'cheapest': {
      text: t('calc__text__delivery__theCheapest'),
      background: '#4DB6AC',
    },
    'popular': {
      text: t('calc__text__delivery__userSelection'),
      background: '#AED581',
    },
  }

  const [ chooseRoute ] = useChooseRouteByOrderIdMutation()

  const orderId = useGetCalcIdFromRoute()
  const readOnly = useCalculationReadOnly()

  if (!route) {
    return null;
  }

  const getRouteDocuments = () => {
    return route.freights.flatMap((freight) => freight.documents || []);
  }

  const getRouteServices = () => {
    return route.freights.flatMap((freight) => freight.services || []);
  }

  const handleChoose = async () => {

    if (isActive || isChanged) {
      handleRemove();
      return;
    }

    const data = {
      orderId,
      route: route.id,
      services: (currentRoute?.id === route.id || currentRouteId === route.id) ? activeAddServicesIds : [],
      planned_shipment_date: route.planned_shipment_date,
      planned_delivery_date: route.planned_delivery_date,
      price_expired_date: route.price_expired_date
    }

    if (isSearchResult) {
      reachGoal({
        goal: 'vibrat_dostavku_m',
        params: {
          route: route.code,
          company: route.freights[0]?.delivery_company?.name,
          location_from: route.location_from.name,
          location_to: route.location_to.name,
          planned_shipment_date: route.planned_shipment_date,
          planned_delivery_date: route.planned_delivery_date,
          total_price: route.total_price,
          currency: route.currency,
        },
      });
    }

    const response = await chooseRoute(data);

    if (isCalculationViewModeError(response)) {
      return;
    }

    setIsChanged(true);
    // @ts-ignore
    if (response?.data?.route){
      setCurrentRouteId(route.id);
      refetch()
    }

    handleClose && handleClose();
  }

  const companyName = route?.freights?.[0]?.delivery_company?.name;

  const routeServices = getRouteServices();
  const routeTerms = getTariffTerms({freights: route.freights, summary});
  const routeDocuments = getRouteDocuments();
  const routeBenefits = route.labels?.filter((routeBenefit: string) => BENEFITS[routeBenefit]) || [];

  const hasTariffDetails = routeServices.length > 0 || routeTerms.length > 0;
  const hasRouteDetails = hasTariffDetails || routeDocuments.length > 0;
  const shouldShowDetails = hasRouteDetails || Boolean(details?.tariffDescription);
  const isCancelAction = isActive || isChanged;
  const useOutlinedAction = isSearchResult && isCancelAction;
  const actionIconColor = useOutlinedAction ? COLORS.BLUE : COLORS.WHITE;

  return <>
    {routeBenefits.length > 0 && <div className={cx('delivery-benefits', {
      'delivery-benefits_search-result': isSearchResult,
    })}>
      {routeBenefits.map((routeBenefit: string, index) =>
        <Card className='delivery-benefit'
          key={index}
          sx={{
            borderRadius: 0,
            boxShadow: 'none',
            background: BENEFITS[routeBenefit].background,
            color: COLORS.WHITE
          }}>
          <Typography variant='subtitle2' sx={isSearchResult ? {
            lineHeight: '21px',
            letterSpacing: 0,
          } : undefined}>
            {BENEFITS[routeBenefit].text}
          </Typography>
        </Card>
      )}
    </div>}
    <Card className={cx('delivery-card', {
      'delivery-card_isChosen': isChosen,
      'delivery-card_search-result': isSearchResult,
    })} sx={{
      background: isActive ? COLORS.LIME : COLORS.WHITE,
      boxShadow: isChosen ? 'none' : undefined,
      borderRadius: 0,
      marginBottom: noConfirmButton ? 0 : 3,
    }}>
      <CardContent sx={{
        padding: 0, '&:last-child': {
          paddingBottom: 0
        }
      }}>
        <div className='delivery-card__layout'>
          <div className='delivery-card__main-content'>
            <div className='delivery-card__main-info'>
              <Typography component='span' variant='inherit' className='delivery-card__logo' sx={{
                fontSize: isSearchResult ? 20 : 24,
                lineHeight: 1,
              }}>{companyName.substring(0, 1)}</Typography>
              <div>
                <Typography variant='subtitle2' sx={isSearchResult ? {
                  color: COLORS.BLACK_DARK,
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '21px',
                  letterSpacing: 0,
                } : undefined}>
                  {companyName}
                </Typography>
                {/*<div className='delivery-card__rating-container'>*/}
                {/*  <Rating name='read-only' value={2} readOnly size='small' />*/}
                {/*  <Typography variant='caption' sx={{ color: 'rgba(0, 0, 0, 0.38)', marginLeft: '4px' }}>*/}
                {/*    (1234)*/}
                {/*  </Typography>*/}
                {/*</div>*/}
              </div>
            </div>
            <Typography variant='caption' sx={{
              color: COLORS.LIGHT_TEXT_SECONDARY,
              marginTop: 2,
              display: 'block',
              fontSize: isSearchResult ? '12px' : undefined,
              fontWeight: isSearchResult ? 400 : undefined,
              lineHeight: isSearchResult ? 1.3 : undefined,
              minHeight: isSearchResult ? '16px' : undefined,
              letterSpacing: isSearchResult ? 0 : undefined,
            }}>
              {t('calc__text__delivery__routeNumber')}: {route.code}
            </Typography>
            {isSearchResult ? <ResultRoute route={route} hintsOpen={details?.routeHintsOpen} /> : <Route route={route}/>}
          </div>
          <div className='delivery-card__sum'>
            <Typography variant='h6' sx={isSearchResult ? {
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '26px',
              letterSpacing: 0,
              color: COLORS.BLACK_DARK,
            } : undefined}>
              {numberWithSpaces(isSearchResult ? route.total_price : route.total_price.toFixed(2))} {route.currency}
            </Typography>
            <Typography variant='caption' sx={{
              color: COLORS.LIGHT_TEXT_SECONDARY,
              fontSize: isSearchResult ? '12px' : undefined,
              fontWeight: isSearchResult ? 400 : undefined,
              lineHeight: isSearchResult ? 1.3 : undefined,
              minHeight: isSearchResult ? '32px' : undefined,
              letterSpacing: isSearchResult ? 0 : undefined,
            }}>
              {t('calc__text__delivery__priceIsAvailable')}
              <br/>{t('common__text__before')} {moment(route.price_expired_date).format('DD.MM.YYYY')}
            </Typography>
            {isSearchResult && shouldShowDetails && <Button
              className='delivery-result-details-button'
              variant='outlined'
              fullWidth
              onClick={() => setDetailsExpanded(!detailsExpanded)}
              aria-expanded={detailsExpanded}
              endIcon={<ExpandMore />}
            >
              <Typography component='span' variant='button'>{t('common__text__detailed')}</Typography>
            </Button>}
          </div>
        </div>
        {shouldShowDetails && <Services
          details={details}
          isSearchResult={isSearchResult}
          expandAll={isSearchResult ? detailsExpanded : undefined}
          isActive={isActive}
          services={routeServices}
          terms={routeTerms}
          documents={routeDocuments}
        />}
        {!noConfirmButton && <div className='delivery-card__confirmButton'>
          <Button
            {...getDemoDeliveryRouteButtonProps(isActive)}
            variant={useOutlinedAction ? 'outlined' : 'contained'}
            fullWidth
            className={cx({ 'read-only-action-button': readOnly })}
            disabled={readOnly}
            onClick={readOnly ? undefined : handleChoose}
            title={readOnly ? t('calc__message__lockedByApplication') : undefined}
            startIcon={isActive || isChanged
              ? <DeleteIcon style={{ color: actionIconColor, width: '24px', marginRight: 0 }} />
              : <CheckIcon style={{ color: COLORS.WHITE, width: '24px', marginRight: 0 }} />}
            endIcon={readOnlyButtonEndIcon(readOnly)}
          >
            <Typography component='span' variant='inherit'>
              {isActive || isChanged ? t('calc__text__delivery__cancelDeliverySelection') : t('calc__text__delivery__chooseDelivery')}
            </Typography>
          </Button>
        </div>}
      </CardContent>
    </Card>
  </>
}

export default DeliveryCard
